import { HAS_SUPABASE, SUPABASE_ANON_KEY, SUPABASE_URL, apiUrl } from './config';
import { wsDiagUrl } from './net';

export interface LatencySummary {
  ok: boolean;
  min: number | null;
  avg: number | null;
  max: number | null;
  samples: number;
  error?: string;
}

export interface DiagnosticsReport {
  online: boolean;
  http: LatencySummary;
  websocket: LatencySummary;
  supabase: LatencySummary | null;
  checkedAt: number;
}

function summarize(samples: number[], ok: boolean, error?: string): LatencySummary {
  if (!samples.length) {
    return { ok: false, min: null, avg: null, max: null, samples: 0, error: error ?? 'no_samples' };
  }
  return {
    ok,
    min: Math.round(Math.min(...samples)),
    avg: Math.round(samples.reduce((sum, value) => sum + value, 0) / samples.length),
    max: Math.round(Math.max(...samples)),
    samples: samples.length,
    ...(error ? { error } : {}),
  };
}

async function measureHttp(
  url: string,
  options: RequestInit = {},
  probes = 4,
): Promise<LatencySummary> {
  const samples: number[] = [];
  let error: string | undefined;
  let allOk = true;
  for (let i = 0; i < probes; i += 1) {
    const started = performance.now();
    try {
      const response = await fetch(url, {
        ...options,
        cache: 'no-store',
        signal: AbortSignal.timeout(9000),
      });
      samples.push(performance.now() - started);
      if (!response.ok) {
        allOk = false;
        error = `http_${response.status}`;
      }
    } catch (caught) {
      error = caught instanceof Error ? caught.name : 'fetch_error';
      allOk = false;
      break;
    }
    await new Promise((resolve) => window.setTimeout(resolve, 90));
  }
  return summarize(samples, allOk, error);
}

async function measureWebSocket(probes = 5): Promise<LatencySummary> {
  return new Promise((resolve) => {
    const samples: number[] = [];
    let settled = false;
    let ws: WebSocket | null = null;
    let guard = 0;
    const timers: number[] = [];
    const finish = (error?: string) => {
      if (settled) return;
      settled = true;
      timers.forEach((timer) => window.clearTimeout(timer));
      window.clearTimeout(guard);
      try {
        ws?.close(1000, 'DIAG_DONE');
      } catch {
        // already closed
      }
      resolve(summarize(samples, !error && samples.length === probes, error));
    };
    guard = window.setTimeout(() => finish('timeout'), 7000);
    try {
      ws = new WebSocket(wsDiagUrl());
    } catch {
      finish('ws_construct_failed');
      return;
    }
    ws.addEventListener('open', () => {
      let sent = 0;
      const send = () => {
        if (settled || !ws) return;
        if (sent >= probes) {
          return;
        }
        sent += 1;
        ws.send(JSON.stringify({ type: 'ping', t: performance.now() }));
        timers.push(window.setTimeout(send, 100));
      };
      send();
    });
    ws.addEventListener('message', (event) => {
      if (typeof event.data !== 'string') return;
      try {
        const message = JSON.parse(event.data) as { type?: string; t?: number };
        if (message.type === 'pong' && typeof message.t === 'number') {
          samples.push(performance.now() - message.t);
          if (samples.length >= probes) {
            timers.push(window.setTimeout(() => finish(), 120));
          }
        }
      } catch {
        // ignore malformed
      }
    });
    ws.addEventListener('error', () => finish('ws_error'));
    ws.addEventListener('close', () => {
      if (!settled) finish(samples.length ? 'closed_early' : 'closed');
    });
  });
}

export async function runNetworkDiagnostics(): Promise<DiagnosticsReport> {
  const http = await measureHttp(apiUrl('/api/health'));
  const websocket = await measureWebSocket();
  let supabase: LatencySummary | null = null;
  if (HAS_SUPABASE) {
    supabase = await measureHttp(
      `${SUPABASE_URL.replace(/\/+$/, '')}/auth/v1/settings`,
      { headers: { apikey: SUPABASE_ANON_KEY } },
      2,
    );
  }
  return {
    online: navigator.onLine,
    http,
    websocket,
    supabase,
    checkedAt: Date.now(),
  };
}

export function formatLatency(summary: LatencySummary | null): string {
  if (!summary) return 'OFFLINE';
  if (!summary.ok || summary.avg === null) {
    return summary.error ? `FAIL / ${summary.error.toUpperCase()}` : 'FAIL';
  }
  return `${summary.min} / ${summary.avg} / ${summary.max} ms`;
}
